import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Line, Pie, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, ArcElement, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { FaSearch, FaSyncAlt, FaMoneyBillWave, FaWallet, FaExchangeAlt, FaUsers, FaClock, FaEnvelope, FaExclamationTriangle } from 'react-icons/fa';
import LoadingImpulse from './LoadingImpulse'; 


ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, BarElement, Title, Tooltip, Legend);

const Overview = () => {
  const [metrics, setMetrics] = useState({
    b2cWorkingBalance: 0,
    b2cUtilityBalance: 0,
    derivPaymentAgentBalance: 0,
    smsBalance: 0,
    totalDeposits: 0,
    totalWithdrawals: 0,
    totalDepositsToday: 0,
    depositsTodayCount: 0,
    totalWithdrawalsToday: 0,
    withdrawalsTodayCount: 0,
    totalTransactions: 0,
    totalProfits: 0,
    totalProfitsToday: 0,
    totalUsers: 0,
    pendingTransactions: 0,
    failedTransactions: 50,
    withdrawalProfitsToday: 0,
    failedTransactions: 50,
    mpesaProcessingTransactions: 0,
    pendingCreditTransactions: 0,
  });

  const [recentTransactions, setRecentTransactions] = useState([]);
  const [trends, setTrends] = useState([]);
  const [dailyVolume, setDailyVolume] = useState([]);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchCollapsed, setIsSearchCollapsed] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [failedTransactions, setFailedTransactions] = useState([]);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [mpesaProcessingTransactions, setMpesaProcessingTransactions] = useState([]);
  const [showMpesaProcessingModal, setShowMpesaProcessingModal] = useState(false);
  const [pendingCreditTransactions, setPendingCreditTransactions] = useState([]);
  const [showPendingCreditModal, setShowPendingCreditModal] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [systemHealth, setSystemHealth] = useState(null);

  // Balance Health Thresholds
  const BALANCE_THRESHOLDS = {
    deriv: { low: 500, medium: 1000 },
    sms: { low: 200, medium: 500 }
  };

  // Dynamic Card Color Logic
  const getBalanceCardColor = (type, balance) => {
    const thresholds = BALANCE_THRESHOLDS[type];
    if (!thresholds) return { bg: 'bg-white', border: 'border-gray-200' };

    if (balance < thresholds.low) {
      return { bg: 'bg-red-50', border: 'border-red-200' };
    } else if (balance < thresholds.medium) {
      return { bg: 'bg-yellow-50', border: 'border-yellow-200' };
    } else {
      return { bg: 'bg-green-50', border: 'border-green-200' };
    }
  };

  const downloadPendingCSV = () => {
  if (!pendingTransactions || pendingTransactions.length === 0) {
    alert('No pending transactions to export');
    return;
    }

    const headers = [
      'Acc Ref',
      'Type',
      'KES',
      'USD',
      'Status',
      'Phone No',
      'Txn Date'
    ];

  const rows = pendingTransactions.map(tx => [
    tx.accountReference || '',
    tx.transactionType || '',
    tx.amount ?? '',
    tx.usdAmount ?? '',
    tx.status || '',
    tx.phoneNumber || '',
    tx.transactionDate
      ? new Intl.DateTimeFormat('en-KE', {
          timeZone: 'Africa/Nairobi',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }).format(new Date(tx.transactionDate))
      : ''
  ]);



    const csvContent =
      [headers, ...rows]
        .map(row =>
          row
            .map(value => `"${String(value).replace(/"/g, '""')}"`)
            .join(',')
        )
        .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `pending_transactions_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };



  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const metricsResponse = await axios.get('https://sys.instant-m-transfer.com/api/admin/metrics', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const withdrawalProfitsResponse = await axios.get('https://sys.instant-m-transfer.com/api/admin/withdrawal-profits-today', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMetrics({
          ...metricsResponse.data.metrics,
          failedTransactions: 50,
          withdrawalProfitsToday: parseFloat(withdrawalProfitsResponse.data.withdrawalProfitsToday) || 0,

          failedTransactions: 50, // Keep as per your code
          mpesaProcessingTransactions: metricsResponse.data.metrics.mpesaProcessingTransactions || 0 // Add this

        });
        const transactionsResponse = await axios.get('https://sys.instant-m-transfer.com/api/admin/transactions', {
          params: { limit: 10, _t: Date.now() },
          headers: { Authorization: `Bearer ${token}` }
        });
        setRecentTransactions(transactionsResponse.data.transactions || []);
        const trendsResponse = await axios.get('https://sys.instant-m-transfer.com/api/admin/transactions/trends', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTrends(trendsResponse.data.trends || []);
        const volumeResponse = await axios.get('https://sys.instant-m-transfer.com/api/admin/transactions/daily-volume', {
          params: { days: 10 },
          headers: { Authorization: `Bearer ${token}` }
        });
        setDailyVolume(volumeResponse.data.dailyVolume || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to fetch data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        
        const healthResponse = await axios.get('https://sys.instant-m-transfer.com/health', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSystemHealth(healthResponse.data);

        const metricsResponse = await axios.get('https://sys.instant-m-transfer.com/api/admin/metrics', {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to fetch data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCompleteAllPending = async () => {
  if (!window.confirm('Complete ALL pending transactions?')) return;

  setIsLoading(true);
  try {
    const token = localStorage.getItem('token');

    await axios.post(
      'https://sys.instant-m-transfer.com/api/admin/transactions/pending/complete-all',
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setPendingTransactions([]);
    setMetrics(prev => ({ ...prev, pendingTransactions: 0 }));
  } catch (err) {
    console.error(err);
    setError('Failed to complete all pending transactions.');
  } finally {
    setIsLoading(false);
  }
};

  const handleCompletePendingCredit = async (transactionId, amountUSD, accountRef) => {
    if (!window.confirm(`Deposit $${amountUSD} to ${accountRef}?`)) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');

      await axios.post(
        `https://sys.instant-m-transfer.com/api/admin/transactions/${transactionId}/complete-manual-deposit`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // remove from UI
      setPendingCreditTransactions(prev =>
        prev.filter(tx => (tx._id || tx.id) !== transactionId)
      );

      alert('Deposit completed');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to complete');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkPendingCreditFailed = async (transactionId) => {
    const reason = window.prompt('Reason (optional):', 'Manual admin action');
    if (reason === null) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');

      await axios.post(
        `https://sys.instant-m-transfer.com/api/admin/transactions/pendingcredit/${transactionId}/mark-failed`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPendingCreditTransactions(prev =>
        prev.filter(tx => (tx._id || tx.id) !== transactionId)
      );

      alert('Marked as failed');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to mark as failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMpesaProcessingClick = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://sys.instant-m-transfer.com/api/admin/transactions/mpesa-processing', {
        params: { limit: 100, _t: Date.now() },
        headers: { Authorization: `Bearer ${token}` }
      });
      setMpesaProcessingTransactions(response.data.transactions || []);
      setShowMpesaProcessingModal(true);
    } catch (err) {
      console.error('Error fetching mpesa processing transactions:', err);
      setError('Failed to fetch mpesa processing transactions. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePendingCreditClick = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');

      const response = await axios.get(
        'https://sys.instant-m-transfer.com/api/admin/transactions/pending-credit',
        {
          params: { limit: 100, _t: Date.now() },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setPendingCreditTransactions(response.data.transactions || []);
      setShowPendingCreditModal(true);

    } catch (err) {
      console.error('Error fetching pending credit transactions:', err);
      setError('Failed to fetch pending credit transactions.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveAllPending = async () => {
    if (!window.confirm('REMOVE ALL pending transactions? This cannot be undone.')) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');

      await axios.post(
        'https://sys.instant-m-transfer.com/api/admin/transactions/pending/remove-all',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPendingTransactions([]);
      setMetrics(prev => ({ ...prev, pendingTransactions: 0 }));
    } catch (err) {
      console.error(err);
      setError('Failed to remove all pending transactions.');
    } finally {
      setIsLoading(false);
    }
  };


  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setSearchError('Please enter a account number (e.g., ROT1234567)');
      setSearchResult(null);
      setShowTransactions(false);
      return;
    }
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://sys.instant-m-transfer.com/api/admin/users/search', {
        params: { accountReference: searchQuery.trim(), _t: Date.now() },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResult(response.data);
      setSearchError('');
      setShowTransactions(false);
    } catch (err) {
      console.error('Search error:', err);
      setSearchError('User not found.');
      setSearchResult(null);
      setShowTransactions(false);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSearchCollapse = () => {
    setIsSearchCollapsed(!isSearchCollapsed);
    if (!isSearchCollapsed) {
      setSearchQuery('');
      setSearchResult(null);
      setSearchError('');
      setShowTransactions(false);
    }
  };

  const handleShowTransactions = () => {
    setShowTransactions(true);
  };

  const closeModal = () => {
    setSearchResult(null);
    setShowTransactions(false);
    setSearchError('');
    setShowPendingModal(false);
    setPendingTransactions([]);
    setShowFailedModal(false);
    setFailedTransactions([]);
    setShowMpesaProcessingModal(false); 
    setMpesaProcessingTransactions([]); 
    setShowPendingCreditModal(false);
    setPendingCreditTransactions([]);
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleRefresh = () => {
    setIsLoading(true);
    setError('');
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const metricsResponse = await axios.get('https://sys.instant-m-transfer.com/api/admin/metrics', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const withdrawalProfitsResponse = await axios.get('https://sys.instant-m-transfer.com/api/admin/withdrawal-profits-today', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMetrics({
          ...metricsResponse.data.metrics,
          failedTransactions: 50,
          withdrawalProfitsToday: parseFloat(withdrawalProfitsResponse.data.withdrawalProfitsToday) || 0
        });
        const transactionsResponse = await axios.get('https://sys.instant-m-transfer.com/api/admin/transactions', {
          params: { limit: 10, _t: Date.now() },
          headers: { Authorization: `Bearer ${token}` }
        });
        setRecentTransactions(transactionsResponse.data.transactions || []);
        const trendsResponse = await axios.get('https://sys.instant-m-transfer.com/api/admin/transactions/trends', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTrends(trendsResponse.data.trends || []);
        const volumeResponse = await axios.get('https://sys.instant-m-transfer.com/api/admin/transactions/daily-volume', {
          params: { days: 10 },
          headers: { Authorization: `Bearer ${token}` }
        });
        setDailyVolume(volumeResponse.data.dailyVolume || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to fetch data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  };

  const handlePendingTransactionsClick = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://sys.instant-m-transfer.com/api/admin/transactions', {
        params: { status: 'pending', limit: 100, _t: Date.now() },
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingTransactions(response.data.transactions || []);
      setShowPendingModal(true);
    } catch (err) {
      console.error('Error fetching pending transactions:', err);
      setError('Failed to fetch pending transactions. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFailedTransactionsClick = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://sys.instant-m-transfer.com/api/admin/transactions', {
        params: {
          status: 'failed',
          limit: 150,
          sort: '-createdAt',
          _t: Date.now()
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setFailedTransactions(response.data.transactions || []);
      setShowFailedModal(true);
    } catch (err) {
      console.error('Error fetching failed transactions:', err);
      setError('Failed to fetch failed transactions. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteTransaction = async (transactionId, amountUSD, accountRef) => {
  const confirmMsg = `Manually deposit $${amountUSD} USD to account ${accountRef}?\n\nThis will use the Deriv Payment Agent Token to transfer real funds.`;
  
  if (!window.confirm(confirmMsg)) return;

  setIsLoading(true);
  try {
    const token = localStorage.getItem('token');
    
    await axios.post(
      `https://sys.instant-m-transfer.com/api/admin/transactions/${transactionId}/complete-manual-deposit`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // Remove from pending list
    setPendingTransactions(prev => prev.filter(tx => (tx._id || tx.id) !== transactionId));
    
    // Update metrics
    setMetrics(prev => ({
      ...prev,
      pendingTransactions: prev.pendingTransactions - 1
    }));

    alert(`Success! $${amountUSD} deposited to ${accountRef}`);
    setError('');
  } catch (err) {
    console.error('Manual deposit failed:', err);
    const msg = err.response?.data?.error || err.message || 'Unknown error';
    alert(`Deposit failed: ${msg}`);
    setError(`Failed to deposit: ${msg}`);
  } finally {
    setIsLoading(false);
  }
};

  const handleRemoveTransaction = async (transactionId) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`https://sys.instant-m-transfer.com/api/admin/transactions/${transactionId}/remove`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingTransactions(prev => prev.filter(tx => (tx._id || tx.id) !== transactionId));
      setMetrics(prev => ({ ...prev, pendingTransactions: prev.pendingTransactions - 1 }));
      setError('');
    } catch (err) {
      console.error('Error removing transaction:', err);
      setError('Failed to remove transaction. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReverseTransaction = async (transactionId, receipt) => {
  if (!window.confirm(`Reverse transaction with receipt ${receipt}?`)) return;

  setIsLoading(true);
  try {
    const token = localStorage.getItem('token');

    await axios.post(
      `https://sys.instant-m-transfer.com/api/admin/transactions/${transactionId}/reverse`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // remove from UI
    setPendingCreditTransactions(prev =>
      prev.filter(tx => (tx.id || tx._id) !== transactionId)
    );

    alert('Transaction reversed successfully');

  } catch (err) {
    console.error('Reverse failed:', err);
    alert(err.response?.data?.error || 'Reverse failed');
  } finally {
    setIsLoading(false);
  }
};

  const lineChartData = {
    labels: trends.map(t => t.month),
    datasets: [
      {
        label: 'Deposits (KES)',
        data: trends.map(t => t.deposits),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.2)',
        tension: 0.3,
        fill: true
      },
      {
        label: 'Withdrawals (KES)',
        data: trends.map(t => t.withdrawals),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.2)',
        tension: 0.3,
        fill: true
      }
    ]
  };

  const pieChartData = {
    labels: ['Deposits', 'Withdrawals'],
    datasets: [
      {
        data: [metrics.totalDeposits, metrics.totalWithdrawals],
        backgroundColor: ['rgb(34, 197, 94)', 'rgb(239, 68, 68)'],
        hoverOffset: 12,
        borderColor: '#fff',
        borderWidth: 2
      }
    ]
  };

  const barChartData = {
    labels: dailyVolume.map(d => d.date),
    datasets: [
      {
        label: 'Transactions',
        data: dailyVolume.map(d => d.count),
        backgroundColor: 'rgb(59, 130, 246)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1000,
      easing: 'easeOutCubic'
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          font: { size: 8, family: 'Inter, sans-serif' },
          color: '#1f2937',
          boxWidth: 10,
          padding: 6
        }
      },
      tooltip: {
        backgroundColor: '#111827',
        titleFont: { size: 8, family: 'Inter, sans-serif' },
        bodyFont: { size: 7, family: 'Inter, sans-serif' },
        padding: 4,
        cornerRadius: 4
      }
    },
    scales: {
      x: {
        ticks: {
          font: { size: 7, family: 'Inter, sans-serif' },
          color: '#1f2937',
          maxRotation: 0,
          autoSkip: true
        },
        grid: { display: false }
      },
      y: {
        ticks: {
          font: { size: 7, family: 'Inter, sans-serif' },
          color: '#1f2937',
          callback: value => value >= 1000 ? `${value / 1000}k` : value
        },
        grid: { color: '#e5e7eb', drawBorder: false }
      }
    }
  };

  const alerts = [];
  if (metrics.derivPaymentAgentBalance < 500) {
    alerts.push('Low Deriv Balance');
  }
  if (metrics.b2cWorkingBalance < 5000) {
    alerts.push('Low B2C Working Balance');
  }
  if (metrics.b2cUtilityBalance < 5000) {
    alerts.push('Low B2C Utility Balance');
  }
  if (metrics.smsBalance < 100) {
    alerts.push('Low SMS Balance');
  }

  const formatKES = (amount) => {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const renderTransactionTable = (
  transactions,
  isPendingTable = false,
  isFailedTable = false,
  isMpesaProcessingTable = false
) => (
  <div className="w-full mt-2 max-h-96 overflow-y-auto">
    {isLoading ? (
      <div className="flex justify-center py-12">
        <LoadingImpulse size="lg" color="blue" />
      </div>
    ) : (
      <table className="w-full">
        <thead className="sticky top-0 bg-gray-100 text-gray-900">
          <tr>
            <th className="p-0.25 text-left text-[6px] font-semibold uppercase border-b border-gray-200 sm:p-2 sm:text-xs min-w-[50px] sm:min-w-[80px]">Acct Ref</th>
            <th className="p-0.25 text-left text-[6px] font-semibold uppercase border-b border-gray-200 sm:p-2 sm:text-xs min-w-[40px] sm:min-w-[80px]">Type</th>
            <th className="p-0.25 text-left text-[6px] font-semibold uppercase border-b border-gray-200 sm:p-2 sm:text-xs min-w-[40px] sm:min-w-[80px]">KES</th>
            <th className="p-0.25 text-left text-[6px] font-semibold uppercase border-b border-gray-200 sm:p-2 sm:text-xs min-w-[40px] sm:min-w-[80px]">USD</th>
            <th className="p-0.25 text-left text-[6px] font-semibold uppercase border-b border-gray-200 sm:p-2 sm:text-xs min-w-[40px] sm:min-w-[80px]">Status</th>
            <th className="p-0.25 text-left text-[6px] font-semibold uppercase border-b border-gray-200 sm:p-2 sm:text-xs min-w-[50px] sm:min-w-[80px]">Date</th>
            <th className="p-0.25 text-left text-[6px] font-semibold uppercase border-b border-gray-200 sm:p-2 sm:text-xs min-w-[40px] sm:min-w-[80px]">Time</th>
            <th className="p-0.25 text-left text-[6px] font-semibold uppercase border-b border-gray-200 sm:p-2 sm:text-xs min-w-[80px] sm:min-w-[120px]">Phone</th>
            
            {isPendingTable && (
              <th className="p-0.25 text-left text-[6px] font-semibold uppercase border-b border-gray-200 sm:p-2 sm:text-xs min-w-[100px] sm:min-w-[140px]">Actions</th>
            )}
            
            {isMpesaProcessingTable && (
              <th className="p-0.25 text-left text-[6px] font-semibold uppercase border-b border-gray-200 sm:p-2 sm:text-xs min-w-[140px] sm:min-w-[180px]">Actions</th>
            )}
            
            {isFailedTable && !isMpesaProcessingTable && (
              <th className="p-0.25 text-left text-[6px] font-semibold uppercase border-b border-gray-200 sm:p-2 sm:text-xs min-w-[100px] sm:min-w-[150px]">Failure Reason</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {transactions.map(tx => {
            const date = tx.transactionDate ? new Date(tx.transactionDate) : (tx.createdAt ? new Date(tx.createdAt) : null);
            const isValidDate = date && !isNaN(date.getTime());

            return (
              <tr key={tx._id || tx.id} className="hover:bg-gray-50 transition-colors duration-150">
                <td className="p-0.25 text-[7px] text-gray-700 sm:p-2 sm:text-sm">{tx.accountReference || 'N/A'}</td>
                <td className="p-0.25 text-[7px] text-gray-700 capitalize sm:p-2 sm:text-sm truncate">{tx.transactionType || 'N/A'}</td>
                <td className="p-0.25 text-[7px] text-gray-700 sm:p-2 sm:text-sm truncate">{typeof tx.amount === 'number' ? Number(tx.amount) : 'N/A'}</td>
                <td className="p-0.25 text-[7px] text-gray-700 sm:p-2 sm:text-sm truncate">{typeof tx.usdAmount === 'number' ? Number(tx.usdAmount) : 'N/A'}</td>
                <td className="p-0.25 sm:p-2">
                  <span className={`inline-block px-0.5 py-0.25 rounded-full text-[7px] font-medium sm:px-2 sm:py-1 sm:text-xs ${
                    tx.status === 'completed' ? 'bg-green-100 text-green-800' :
                    tx.status === 'pending' || tx.status === 'mpesa_processing' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {tx.status || 'N/A'}
                  </span>
                </td>
                <td className="p-0.25 text-[7px] text-gray-700 sm:p-2 sm:text-sm">
                  {isValidDate ? date.toLocaleDateString('en-US', { timeZone: 'Africa/Nairobi', year: '2-digit', month: '2-digit', day: '2-digit' }) : 'Invalid Date'}
                </td>
                <td className="p-0.25 text-[7px] text-gray-700 sm:p-2 sm:text-sm truncate">
                  {isValidDate ? date.toLocaleTimeString('en-US', { timeZone: 'Africa/Nairobi', hour: '2-digit', minute: '2-digit', hour12: false }) : 'N/A'}
                </td>
                <td className="p-0.25 text-[6px] text-gray-700 sm:p-2 sm:text-xs">
                  {tx.phoneNumber ? `${tx.phoneNumber.slice(0, 3)} ${tx.phoneNumber.slice(3, 6)} ${tx.phoneNumber.slice(6)}` : 'N/A'}
                </td>

                {/* Pending Actions */}
                {isPendingTable && (
                    <td className="p-0.25 text-[6px] text-gray-700 sm:p-2 sm:text-xs min-w-[80px] sm:min-w-[120px]">
                      <div className="flex gap-1">
                      <button
                        onClick={() => handleCompleteTransaction(
                          tx._id || tx.id,
                          Number(tx.usdAmount) || '0.00',
                          tx.accountReference || 'Unknown'
                        )}
                        className="px-1 py-0.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-800 hover:to-green-900 text-[8px] font-medium sm:px-2 sm:py-1 sm:text-xs transition-all duration-200 disabled:opacity-50"
                        disabled={isLoading}
                      >
                        Deposit & Complete
                      </button>
                        <button
                          onClick={() => handleRemoveTransaction(tx._id || tx.id)}
                          className="px-1 py-0.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-[8px] font-medium sm:px-2 sm:py-1 sm:text-xs transition-all duration-200"
                          disabled={isLoading}
                        >
                          Mark failed
                        </button>
                      </div>
                    </td>
                  )}

                {/* M-Pesa Processing Actions */}
                {isMpesaProcessingTable && (
                  <td className="p-0.25 text-[6px] sm:p-2 sm:text-xs">
                    <div className="flex gap-1 flex-wrap">
                      <button
                        onClick={async () => {
                          const transactionId = tx._id || tx.id; // fallback to tx.id if _id missing
                          if (!transactionId) {
                            alert("Error: Transaction ID not found. Please refresh the page.");
                            return;
                          }

                          if (!window.confirm(`Sending KES ${Number(tx.amount)} to ${tx.phoneNumber}?`)) return;

                          setIsLoading(true);
                          try {
                            const token = localStorage.getItem('token');
                            await axios.post(
                              `https://sys.instant-m-transfer.com/api/admin/transactions/${transactionId}/retry-b2c`,
                              {},
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            alert('B2C Withdrawal Successful.');
                            setMpesaProcessingTransactions(prev => prev.filter(t => (t._id || t.id) !== transactionId));
                            setMetrics(prev => ({ ...prev, mpesaProcessingTransactions: prev.mpesaProcessingTransactions - 1 }));
                          } catch (err) {
                            alert('Retry failed: ' + (err.response?.data?.error || err.message));
                          } finally {
                            setIsLoading(false);
                          }
                        }}
                        className="px-1 py-0.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-[8px] font-medium sm:px-2 sm:py-1 sm:text-xs transition-all duration-200 disabled:opacity-50"
                        disabled={isLoading}
                      >
                        Retry B2C
                      </button>
                      <button
                        onClick={async () => {
                          const transactionId = tx._id || tx.id;
                          if (!transactionId) {
                            alert("Error: Transaction ID not found. Please refresh the page.");
                            return;
                          }

                          const reason = window.prompt('Reason (optional):', 'Timeout / insufficient funds');
                          if (reason === null) return;

                          setIsLoading(true);
                          try {
                            const token = localStorage.getItem('token');
                            await axios.post(
                              `https://sys.instant-m-transfer.com/api/admin/transactions/${transactionId}/mark-failed`,
                              { reason },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            alert('Marked as failed');
                            setMpesaProcessingTransactions(prev => prev.filter(t => (t._id || t.id) !== transactionId));
                            setMetrics(prev => ({ ...prev, mpesaProcessingTransactions: prev.mpesaProcessingTransactions - 1 }));
                          } catch (err) {
                            alert('Failed: ' + (err.response?.data?.error || err.message));
                          } finally {
                            setIsLoading(false);
                          }

                        }}
                        className="px-1 py-0.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-[8px] font-medium sm:px-2 sm:py-1 sm:text-xs transition-all duration-200"
                        disabled={isLoading}
                      >
                        Mark Failed
                      </button>
                    </div>
                  </td>
                )}

                {/* Failure Reason only for failed transactions */}
                {isFailedTable && !isMpesaProcessingTable && (
                  <td className="p-0.25 text-[6px] text-gray-700 sm:p-2 sm:text-xs truncate">
                    {tx.failureReason || 'N/A'}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    )}
  </div>
);

  const renderSkeletonCards = () => (
    <div className="grid grid-cols-3 gap-1 mb-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 sm:gap-4">
      {[...Array(13)].map((_, index) => (
        <div key={index} className="bg-white p-1 rounded-lg shadow border border-gray-200 animate-pulse sm:p-2 h-20">
          <div className="h-2 bg-gray-200 rounded w-3/4 mb-1 sm:h-4 sm:mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2 sm:h-6"></div>
        </div>
      ))}
      <div className="col-span-3 bg-white p-1 rounded-lg shadow border border-gray-200 animate-pulse sm:p-2 h-20">
        <div className="h-2 bg-gray-200 rounded w-3/4 mb-1 sm:h-4 sm:mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2 sm:h-6"></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-2 px-1 sm:py-4 sm:px-2 md:py-6 md:px-4 bg-gray-50">
      <div className="max-w-full mx-auto sm:max-w-7xl">
        <div className="top-0 z-10 bg-gray-50 py-2 sm:flex sm:justify-between sm:items-center">
          <h4 className="text-xs font-bold font-sftsMedium sm:text-lg md:text-xl">Dashboard Overview</h4>
          <div className="flex gap-1 mt-1 sm:mt-0">
            <button
              onClick={handleRefresh}
              className="px-2 py-0.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 text-[10px] font-medium transition-all duration-200 flex items-center gap-0.5 sm:px-4 sm:py-2 sm:text-sm"
            >
              <FaSyncAlt className={isLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {systemHealth && (
          <div className="bg-white border border-gray-200 rounded-lg p-2 mb-3 shadow-sm flex flex-wrap items-center justify-between gap-2 text-[10px] sm:text-xs">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-700">System Status:</span>
              <span className={`px-2 py-0.5 rounded-full font-bold ${systemHealth.status === 'running' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {systemHealth.status}
              </span>
            </div>
            <div className="flex items-center gap-4 text-gray-600">
              <span><strong>Database:</strong> <span className={systemHealth.dbStatus === 'connected' ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>{systemHealth.dbStatus}</span></span>
              <span><strong>M-Pesa Env:</strong> <span className="uppercase font-bold text-blue-600">{systemHealth.mpesaEnv}</span></span>
              <span><strong>Uptime:</strong> <span className='font-bold'> {Math.floor(systemHealth.uptime / 60)}m {Math.floor(systemHealth.uptime % 60)}s </span></span>
            </div>
          </div>
        )}

        <div className="my-2">
          <div className="flex justify-between items-center mb-1">
            <h2 className="text-[10px] font-semibold font-sftsMedium sm:text-sm md:text-base">Search User</h2>
          </div>
          {!isSearchCollapsed && (
            <div className="transition-all duration-300 ease-in-out flex justify-center items-center">
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row items-center gap-1">
                <div className="relative w-40 sm:w-64">
                  <FaSearch className="absolute left-1 top-1/2 transform -translate-y-1/2 text-gray-500 text-[10px] sm:text-sm" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="ROT12345678"
                    className="w-full pl-6 pr-1 py-0.5 border border-gray-300 bg-white text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-[10px] sm:pl-10 sm:pr-3 sm:py-2 sm:text-sm shadow-sm transition-all duration-200"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-20 sm:w-auto px-2 py-0.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 text-[10px] font-medium transition-all duration-200 flex items-center gap-0.5 disabled:opacity-50 disabled:cursor-not-allowed sm:px-4 sm:py-2 sm:text-sm"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-3 w-3 text-white sm:h-5 sm:w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Searching
                    </>
                  ) : (
                    'Search'
                  )}
                </button>
              </form>
              {searchError && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                  <div className="p-2 rounded-lg shadow-xl w-full max-w-[90%] bg-white text-gray-900 animate-fade-in sm:p-4 sm:max-w-sm">
                    <div className="flex justify-between items-center mb-1">
                      <h3 className="text-[10px] font-semibold text-red-600 sm:text-base">Error</h3>
                      <button onClick={closeModal} className="text-gray-600 hover:text-gray-800 text-sm font-bold sm:text-lg">X</button>
                    </div>
                    <p className="text-[10px] text-red-600 font-['Open_Sans'] sm:text-sm">{searchError}</p>
                    <button
                      onClick={closeModal}
                      className="mt-2 w-full px-2 py-0.5 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 text-[10px] font-medium font-['Open_Sans'] transition-all duration-200 sm:px-4 sm:py-2 sm:text-sm"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
              {searchResult && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                  <div className="p-2 rounded-lg shadow-xl w-full max-w-[90%] bg-white text-gray-900 animate-fade-in sm:p-4 sm:max-w-sm">
                    <div className="flex justify-between items-center mb-1">
                      <h3 className="text-[10px] font-semibold font-sftsMedium sm:text-base">User Details</h3>
                      <button onClick={closeModal} className="text-gray-600 hover:text-gray-800 text-sm font-bold sm:text-lg">X</button>
                    </div>
                    <div className="text-[10px] font-['Open_Sans'] space-y-0.5 sm:text-sm sm:space-y-2">
                      <p><strong>NickName:</strong> {searchResult.user.nickname}</p>
                      <p><strong>Deriv Account:</strong> {searchResult.user.deriv_account}</p>
                      <p><strong>Balance:</strong> ${searchResult.user.balance} USD</p>
                    </div>
                    {!showTransactions && (
                      <button
                        onClick={handleShowTransactions}
                        className="mt-2 w-full px-2 py-0.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 text-[10px] font-medium font-['Open_Sans'] transition-all duration-200 sm:px-4 sm:py-2 sm:text-sm"
                      >
                        View Transactions
                      </button>
                    )}
                    {showTransactions && (
                      <div className="mt-0">
                        <div className="flex justify-between items-center mb-1">
                          <h3 className="text-[10px] font-semibold font-sftsMedium sm:text-sm">User Transactions</h3>
                        </div>
                        {searchResult.transactions.length === 0 ? (
                          <p className="text-[10px] text-gray-500 italic font-['Open_Sans'] sm:text-sm">No transactions found.</p>
                        ) : (
                          renderTransactionTable(searchResult.transactions, false, false)
                        )}
                      </div>
                    )}
                    <button
                      onClick={closeModal}
                      className="mt-2 w-full px-2 py-0.5 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 text-[10px] font-medium font-['Open_Sans'] transition-all duration-200 sm:px-4 sm:py-2 sm:text-sm"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pending & Failed Modals */}
        {showPendingModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="p-2 rounded-lg shadow-xl w-full max-w-[90%] bg-white text-gray-900 animate-fade-in sm:p-4 sm:max-w-4xl">
          <div className="flex flex-col gap-2 mb-2">
            <div className="flex justify-between items-center">
              <h3 className="text-[10px] font-semibold font-sftsMedium sm:text-base">
                Pending Transactions
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-600 hover:text-gray-800 text-sm font-bold sm:text-lg"
              >
                X
              </button>
            </div>

            {/* Bulk Action Btn */}
              {pendingTransactions.length > 0 && (
                <div className="flex justify-end gap-2 flex-wrap">
                  <button
                    onClick={downloadPendingCSV}
                    className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-[10px] sm:text-xs"
                  >
                    Download CSV
                  </button>

                  <button
                    onClick={handleRemoveAllPending}
                    disabled={isLoading}
                    className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-[10px] sm:text-xs disabled:opacity-50"
                  >
                    Mark All as Failed
                  </button>
                </div>
              )}
            </div>

              {pendingTransactions.length === 0 ? (
                <p className="text-[10px] text-gray-500 italic font-['Open_Sans'] sm:text-sm">No pending transactions found.</p>
              ) : (
                renderTransactionTable(pendingTransactions, true, false)
              )}
              <button
                onClick={closeModal}
                className="mt-2 w-full px-2 py-0.5 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 text-[10px] font-medium font-['Open_Sans'] transition-all duration-200 sm:px-4 sm:py-2 sm:text-sm"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {showFailedModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="p-2 rounded-lg shadow-xl w-full max-w-[90%] bg-white text-gray-900 animate-fade-in sm:p-4 sm:max-w-4xl">
              <div className="flex justify-between items-center mb-1">
                <h3 className="text-[10px] font-semibold font-sftsMedium sm:text-base">Failed Transactions</h3>
                <button onClick={closeModal} className="text-gray-600 hover:text-gray-800 text-sm font-bold sm:text-lg">X</button>
              </div>
              {failedTransactions.length === 0 ? (
                <p className="text-[10px] text-gray-500 italic font-['Open_Sans'] sm:text-sm">No failed transactions found.</p>
              ) : (
                renderTransactionTable(failedTransactions, false, true)
              )}
              <button
                onClick={closeModal}
                className="mt-2 w-full px-2 py-0.5 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 text-[10px] font-medium font-['Open_Sans'] transition-all duration-200 sm:px-4 sm:py-2 sm:text-sm"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {showPendingCreditModal && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
              <div className="p-2 rounded-lg shadow-xl w-full max-w-[90%] bg-white text-gray-900 animate-fade-in sm:p-4 sm:max-w-4xl">
                
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-[10px] font-semibold sm:text-base">
                    Pending Credit Transactions ({pendingCreditTransactions.length})
                  </h3>
                  <button
                    onClick={closeModal}
                    className="text-gray-600 hover:text-gray-800 font-bold"
                  >
                    X
                  </button>
                </div>

                {pendingCreditTransactions.length === 0 ? (
                  <p className="text-[10px] text-gray-500 italic sm:text-sm">
                    No pending credit transactions found.
                  </p>
                ) : (
                  <div className="w-full mt-2 max-h-96 overflow-y-auto">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-gray-100">
                        <tr>
                          <th className="p-1 text-left text-xs">Acct</th>
                          <th className="p-1 text-left text-xs">KES</th>
                          <th className="p-1 text-left text-xs">USD</th>
                          <th className="p-1 text-left text-xs">Receipt</th>
                          <th className="p-1 text-left text-xs">Reason</th>
                          <th className="p-1 text-left text-xs">Phone</th>
                          <th className="p-1 text-left text-xs">Date</th>
                          <th className="p-1 text-left text-xs">Action</th>

                        </tr>
                      </thead>

                      <tbody>
                        {pendingCreditTransactions.map(tx => (
                          <tr key={tx.id || tx._id} className="border-b">
                            <td className="p-1 text-xs">{tx.accountReference}</td>
                            <td className="p-1 text-xs">{tx.amountKES}</td>
                            <td className="p-1 text-xs">{tx.amountUSD}</td>
                            <td className="p-1 text-xs font-mono">{tx.mpesaReceiptNumber}</td>
                            <td className="p-1 text-xs max-w-[180px]">
                              <div className="flex items-start gap-1">
                                
                                {/* Text */}
                                <span className={expandedRows[tx._id] ? '' : 'truncate block max-w-[140px]'}>
                                  {tx.failureReason}
                                </span>

                                {/* Chevron */}
                                {tx.failureReason?.length > 30 && (
                                  <button
                                    onClick={() => toggleRow(tx._id)}
                                    className="text-gray-500 hover:text-black"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className={`w-4 h-4 transition-transform ${
                                        expandedRows[tx._id] ? 'rotate-180' : ''
                                      }`}
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M19 9l-7 7-7-7"
                                      />
                                    </svg>
                                  </button>
                                )}
                                
                              </div>
                            </td>
                            <td className="p-1 text-xs">{tx.phoneNumber}</td>
                            <td className="p-1 text-xs">
                              {tx.transactionDate 
                                ? new Date(tx.transactionDate).toLocaleString('en-GB', { 
                                    timeZone: 'Africa/Nairobi',
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  }) 
                                : 'N/A'}
                            </td>

                            <td className="p-1">
                                <div className="flex gap-1 flex-wrap">

                                  {/* Complete */}
                                  <button
                                    onClick={() =>
                                      handleCompletePendingCredit(
                                        tx._id || tx.id,
                                        tx.amountUSD,
                                        tx.accountReference
                                      )
                                    }
                                    className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                                    disabled={isLoading}
                                  >
                                    Complete
                                  </button>
                                                                    {/* Existing Reverse */}
                                  <button
                                    onClick={() =>
                                      handleReverseTransaction(
                                        tx.id || tx._id,
                                        tx.mpesaReceiptNumber
                                      )
                                    }
                                    className="px-2 py-1 bg-gray-700 text-white rounded text-xs hover:bg-gray-800"
                                    disabled={isLoading}
                                  >
                                    Reverse
                                  </button>

                                  {/* Mark Failed */}
                                  <button
                                    onClick={() =>
                                      handleMarkPendingCreditFailed(tx._id || tx.id)
                                    }
                                    className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                                    disabled={isLoading}
                                  >
                                    Fail
                                  </button>



                                </div>
                              </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <button
                  onClick={closeModal}
                  className="mt-2 w-full px-2 py-1 bg-gray-700 text-white rounded text-xs"
                >
                  Close
                </button>
              </div>
            </div>
          )}

      {showMpesaProcessingModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="p-2 rounded-lg shadow-xl w-full max-w-[90%] bg-white text-gray-900 animate-fade-in sm:p-4 sm:max-w-4xl">
              <div className="flex flex-col gap-2 mb-2">
                <div className="flex justify-between items-center">
                  <h3 className="text-[10px] font-semibold font-sftsMedium sm:text-base">
                    M-Pesa Processing Transactions ({mpesaProcessingTransactions.length})
                  </h3>
                  <button onClick={closeModal} className="text-gray-600 hover:text-gray-800 text-sm font-bold sm:text-lg">X</button>
                </div>

                {/* Bulk Mark All as Failed */}
                {mpesaProcessingTransactions.length > 0 && (
                  <div className="flex justify-end">
                    <button
                      onClick={async () => {
                        if (!window.confirm('Mark ALL M-Pesa Processing transactions as FAILED? This cannot be undone.')) return;
                        setIsLoading(true);
                        try {
                          const token = localStorage.getItem('token');
                          await axios.post(
                            'https://sys.instant-m-transfer.com/api/admin/transactions/mpesa-processing/mark-all-failed',
                            { reason: 'Bulk manual resolution - likely insufficient funds or timeout' },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          alert('All transactions marked as failed');
                          setShowMpesaProcessingModal(false);
                          handleRefresh();
                        } catch (err) {
                          alert('Bulk action failed: ' + (err.response?.data?.error || err.message));
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                      disabled={isLoading}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-[10px] sm:text-sm disabled:opacity-50"
                    >
                      Mark All as Failed
                    </button>
                  </div>
                )}
              </div>

              {mpesaProcessingTransactions.length === 0 ? (
                <p className="text-[10px] text-gray-500 italic font-['Open_Sans'] sm:text-sm">
                  No mpesa processing transactions found.
                </p>
              ) : (
                renderTransactionTable(mpesaProcessingTransactions, false, false, true) // true = isMpesaProcessingTable
              )}

              <button
                onClick={closeModal}
                className="mt-2 w-full px-2 py-0.5 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 text-[10px] font-medium font-['Open_Sans'] transition-all duration-200 sm:px-4 sm:py-2 sm:text-sm"
              >
                Close
              </button>
            </div>
          </div>
      )}

        {error && (
          <div className="border-l-4 border-red-500 p-1 mb-2 rounded-r-lg shadow bg-red-50 sm:p-3 sm:mb-6">
            <p className="text-red-700 text-[10px] font-medium font-['Open_Sans'] sm:text-sm">{error}</p>
          </div>
        )}

        {alerts.length > 0 && (
          <div className="border-l-4 border-yellow-500 p-1 mb-2 rounded-r-lg shadow bg-yellow-50 sm:p-3 sm:mb-6">
            <h2 className="text-[10px] font-semibold font-sftsMedium mb-0.5 sm:text-sm sm:mb-2">Alerts</h2>
            <ul className="list-disc pl-3 space-y-0.5 sm:pl-5">
              {alerts.map((alert, index) => (
                <li key={index} className="text-[10px] text-yellow-700 font-['Open_Sans'] sm:text-sm">{alert}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Metrics Cards */}
        {isLoading ? (
                    <div className="flex justify-center py-12">
                      <LoadingImpulse size="lg" color="blue" />
                    </div>
        ) : (
          <div className="grid grid-cols-3 gap-1 mb-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 sm:gap-4">
            {[
              { 
                title: 'Deriv Balance', 
                value: `$${Number(metrics.derivPaymentAgentBalance?.balance ?? 0).toFixed(2)}`, 
                icon: <FaWallet className="text-blue-900 text-[10px] sm:text-base" />,
                balanceType: 'deriv',
                balanceValue: metrics.derivPaymentAgentBalance?.balance ?? 0
              },
              { 
                title: 'SMS Balance', 
                value: `${metrics.smsBalance} Units`, 
                icon: <FaEnvelope className="text-blue-900 text-[10px] sm:text-base" />,
                balanceType: 'sms',
                balanceValue: metrics.smsBalance
              },
              { title: 'Total Deposits', value: `${metrics.totalDeposits} KES`, icon: <FaExchangeAlt className="text-blue-900 text-[10px] sm:text-base" /> },
              { title: 'Total Withdrawals', value: `${metrics.totalWithdrawals} KES`, icon: <FaExchangeAlt className="text-blue-900 text-[10px] sm:text-base" /> },
              {
                title: `Deposits Today (${metrics.depositsTodayCount})`,
                value: `$${Number(metrics.totalDepositsToday)} || KES ${formatKES(metrics.totalDepositsToday * 133)}`,
                icon: <FaExchangeAlt className="text-blue-900 text-[10px] sm:text-base" />
              },
              {
                title: `Withdrawals Today (${metrics.withdrawalsTodayCount})`,
                value: `$${Number(metrics.totalWithdrawalsToday)} || KES ${formatKES(metrics.totalWithdrawalsToday * 125)}`,
                icon: <FaExchangeAlt className="text-blue-900 text-[10px] sm:text-base" />
              },
              {
                title: 'Withdrawals Profit Today',
                value: `${formatKES(metrics.withdrawalProfitsToday)} KES || $${(metrics.withdrawalProfitsToday / 129).toFixed(2)} USD`,
                icon: <FaMoneyBillWave className="text-blue-900 text-[10px] sm:text-base" />
              },
              { title: 'Total Transactions', value: metrics.totalTransactions, icon: <FaExchangeAlt className="text-blue-900 text-[10px] sm:text-base" /> },
              { title: 'Total Profits', value: `${((metrics.totalWithdrawals / 133) * 8).toFixed(2)} KES`, icon: <FaMoneyBillWave className="text-blue-900 text-[10px] sm:text-base" /> },
              { title: 'Total Users', value: metrics.totalUsers, icon: <FaUsers className="text-blue-900 text-[10px] sm:text-base" /> },
              {
                title: `Pending Transactions (${metrics.pendingTransactions})`,
                value: '',
                icon: <FaClock className="text-blue-900 text-[10px] sm:text-base" />,
                onClick: handlePendingTransactionsClick
              },
              {
                title: `Pending Credit (${metrics.pendingCreditTransactions})`,
                value: '',
                icon: <FaExclamationTriangle className="text-blue-900 text-[10px] sm:text-base" />,
                onClick: handlePendingCreditClick
              },
              {
                title: `Failed Transactions (${metrics.failedTransactions})`,
                value: '',
                icon: <FaExclamationTriangle className="text-blue-900 text-[10px] sm:text-base" />,
                onClick: handleFailedTransactionsClick
              },
              {
                title: `M-Pesa Processing (${metrics.mpesaProcessingTransactions})`,
                value: '',
                icon: <FaClock className="text-blue-900 text-[10px] sm:text-base" />,
                onClick: handleMpesaProcessingClick
              },

              {
                title: 'B2C Balance',
                value: `Working Acc: ${metrics.b2cWorkingBalance} KES\nUtility Acc: ${metrics.b2cUtilityBalance} KES`,
                icon: <FaMoneyBillWave className="text-blue-900 text-[10px] sm:text-base" />,
                isB2C: true
              },


            ].map((metric, index) => {
              const color = metric.balanceType 
                ? getBalanceCardColor(metric.balanceType, metric.balanceValue)
                : { bg: 'bg-white', border: 'border-gray-200' };

              return (
                <div
                  key={index}
                  onClick={metric.onClick}
                  className={`p-1 rounded-lg shadow-md border ${color.bg} ${color.border} sm:p-3 ${
                    metric.isB2C ? 'col-span-3' : 'h-15 flex flex-col justify-between'
                  } ${metric.onClick ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`}
                >
                  <div className="flex items-center gap-0.5 sm:gap-2">
                    {metric.icon}
                    <h2 className="text-[10px] font-medium font-sftsMedium text-gray-600 truncate sm:text-sm">{metric.title}</h2>
                  </div>
                  <p className="text-xs font-semibold text-blue-900 whitespace-pre-line sm:text-base">{metric.value}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Recent Transactions */}
        <div className="p-1 rounded-lg shadow-md border bg-white border-gray-200 mb-2 sm:p-3 sm:mb-6">
          <div className="flex justify-between items-center mb-1 sm:mb-3">
            <h2 className="text-[10px] font-semibold font-sftsMedium sm:text-sm">Recent Transactions</h2>
            <Link
              to="/transactions"
              className="px-1 py-0.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 text-[10px] font-sftsMedium font-['Open_Sans'] transition-all duration-200 sm:px-3 sm:py-1 sm:text-sm"
            >
              View All
            </Link>
          </div>
          {recentTransactions.length === 0 && !isLoading ? (
            <p className="text-[10px] text-gray-500 italic font-['Open_Sans'] sm:text-sm">No transactions.</p>
          ) : (
            renderTransactionTable(recentTransactions, false, false)
          )}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 gap-2 mb-2 sm:gap-6 sm:mb-6">
          <div className="p-1 rounded-lg shadow-md border bg-white border-gray-200 sm:p-3">
            <h2 className="text-[10px] font-semibold font-sftsMedium mb-1 sm:text-sm sm:mb-3">Transaction Trends</h2>
            <div className="relative h-40 w-full sm:h-64">
              <Line data={lineChartData} options={chartOptions} />
            </div>
          </div>
          <div className="p-1 rounded-lg shadow-md border bg-white border-gray-200 sm:p-3">
            <h2 className="text-[10px] font-semibold font-sftsMedium mb-1 sm:text-sm sm:mb-3">Transaction Breakdown</h2>
            <div className="relative h-40 w-full flex justify-center sm:h-64">
              <Pie data={pieChartData} options={chartOptions} />
            </div>
          </div>
          <div className="p-1 rounded-lg shadow-md border bg-white border-gray-200 sm:p-3">
            <h2 className="text-[10px] font-semibold font-sftsMedium mb-1 sm:text-sm sm:mb-3">Daily Volume</h2>
            <div className="relative h-40 w-full sm:h-64">
              <Bar data={barChartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&display=swap');
        .animate-fade-in {
          animation: fadeIn 0.3s ease-in-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Overview;